/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100190 ロケーション管理マスタメンテ
 */
import { Column } from "@material-table/core";
import {
  B100190Create,
  B100190CreateRequest,
  B100190Delete,
  B100190DeleteRequest,
  B100190Find,
  B100190FindRequest,
  B100190Update,
  B100190UpdateRequest,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SCodeSelect,
  SConfirmDialog,
  SConfirmDialogAction,
  SEntryTab,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import {
  getSCode,
  NyukolocationKbn,
  SCode,
  ShukolocationKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, navigateTypeState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
// ヘッダ部
type Fields = Partial<{
  headerZoneCd: string;
  headerRetu: string;
  headerRen: string;
}>;

// 明細入力部
type MeiFields = Partial<{
  tab: boolean;
  locationCd: string;
  locationCdF: string;
  ren: string;
  dan: string;
  maguti: string;
  nyukolocationKbn: SCode;
  shukolocationKbn: SCode;
  version: number;
}>;

//テーブルデータの定義
type Data = Partial<{
  gyoNo: number;
  locationCdF: string;
  locationCd: string;
  ren: string;
  dan: string;
  maguti: string;
  nyukolocationKbn: string;
  shukolocationKbn: string;
  version: number;
}>;

// フィールドの初期値を設定する。
// ヘッダ部
const defaultFields: Fields = {
  headerZoneCd: "",
  headerRetu: "",
  headerRen: "",
};

// 明細入力部
const defaultMeiFields: MeiFields = {
  tab: true,
  locationCd: "",
  locationCdF: "",
  ren: "",
  dan: "",
  maguti: "",
  nyukolocationKbn: getSCode(NyukolocationKbn, "0"),
  shukolocationKbn: getSCode(ShukolocationKbn, "0"),
  version: undefined,
};

const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "3rem", align: "right" },
  { field: "locationCdF", title: "ロケーションコード", width: "5rem" },
  { field: "ren", title: "連", width: "3rem" },
  { field: "dan", title: "段", width: "3rem" },
  { field: "maguti", title: "間口", width: "5rem" },
  { field: "nyukolocationKbn", title: "入庫ロケ区分", width: "5rem" },
  { field: "shukolocationKbn", title: "出庫ロケ区分", width: "5rem" },
  { field: "locationCd", hidden: true },
  { field: "version", hidden: true },
];

const focusId = "B100190";

// B100190 ロケーション管理マスタメンテ
export const B100190 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  const meiMethods = useForm<MeiFields>({
    mode: "onBlur",
    defaultValues: defaultMeiFields,
  });

  // テーブル管理
  const tableRef = createRef();
  const [rows, setRows] = useState<Data[]>([]);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 更新確認ダイアログオープン用
  const [openUpdateConfirm, setOpenUpdateConfirm] = useState(false);
  // 削除確認ダイアログオープン用
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  // 画面遷移の処理
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);

  // 初期表示イベント
  useEffect(() => {
    focus(1);
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索フォームのデータを取得する。
    const fields = methods.getValues();

    // 検索パラメータを作成する。
    const param: B100190FindRequest = {
      headerZoneCd: fields.headerZoneCd!,
      headerRetu: fields.headerRetu!,
      headerRen: fields.headerRen!,
    };

    // API呼び出し
    B100190Find(param)
      // 正常取得時
      .then((value) => {
        // グリッドへのマッピング
        const gridDataList = value.data.map<Data>((value, index) => {
          // 行番号
          const gyoNo: number = index + 1;
          // 行のデータセット
          const data: Data = {
            gyoNo: Number(gyoNo),
            locationCdF: value.locationCdF,
            ren: value.ren,
            dan: value.dan,
            maguti: value.maguti,
            nyukolocationKbn: value.nyukolocationKbn,
            shukolocationKbn: value.shukolocationKbn,
            locationCd: value.locationCd,
            version: value.version,
          };
          return data;
        });
        // テーブルに反映
        setRows(gridDataList);
      })
      // エラー時
      .catch((reason) => {
        setRows([]);
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 登録
  const handleCreate = () => {
    // タブ部のデータ取得
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();

    // 新規登録するデータの作成
    const param: B100190CreateRequest = {
      headerZoneCd: fields.headerZoneCd!,
      headerRetu: fields.headerRetu!,
      headerRen: fields.headerRen!,
      dan: meiFields.dan!,
      maguti: meiFields.maguti!,
      nyukolocationKbn: meiFields.nyukolocationKbn?.value!,
      shukolocationKbn: meiFields.shukolocationKbn?.value!,
    };

    B100190Create(param)
      .then(() => {
        // 新規登録したデータの行追加
        addRow(param);
        // タブ部の各項目を初期化する
        meiMethods.reset();
        // 登録完了メッセージ表示
        setMsg(getClientMessage("B100190_001"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 更新確認ダイアログ表示
  const handleOpenUpdateConfirm = () => {
    setOpenUpdateConfirm(true);
  };

  // 削除確認ダイアログキャンセル
  const handleCancelUpdateConfirm = () => {
    setOpenUpdateConfirm(false);
  };

  // 更新
  const handleUpdate = () => {
    setOpenUpdateConfirm(false);
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();

    // 更新するデータの作成
    const param: B100190UpdateRequest = {
      locationCd: meiFields.locationCd!,
      dan: meiFields.dan!,
      maguti: meiFields.maguti!,
      nyukolocationKbn: meiFields.nyukolocationKbn?.value!,
      shukolocationKbn: meiFields.shukolocationKbn?.value!,
      version: meiFields.version!,
    };

    B100190Update(param)
      .then(() => {
        // 更新した行の表示更新
        updateRow(rowData, fields, meiFields);
        // タブ部の初期化
        meiMethods.reset();
        // 更新完了メッセージ表示
        setMsg(getClientMessage("B100190_002"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 削除確認ダイアログ表示
  const handleOpenDeleteConfirm = () => {
    setOpenDeleteConfirm(true);
  };

  // 削除確認ダイアログキャンセル
  const handleCancelDeleteConfirm = () => {
    setOpenDeleteConfirm(false);
  };

  // 削除
  const handleDelete = () => {
    setOpenDeleteConfirm(false);

    const meiFields = meiMethods.getValues();

    const param: B100190DeleteRequest = {
      locationCd: meiFields.locationCd!,
    };

    B100190Delete(param)
      .then(() => {
        // 選択行の削除処理
        // 選択行の行番号取得
        const gyoNo = rowData?.gyoNo;
        // 取得した行番号の行を除いた全てのデータを取得
        const newRows = rows.filter(function (source: Data) {
          return source.gyoNo !== gyoNo;
        });
        // 明細テーブルの行番号振り直し
        mapIds(newRows, "gyoNo");
        // 削除後のデータセット
        setRows(newRows);
        // 明細入力部の初期化
        meiMethods.reset();
        // タブを新規にする
        meiMethods.setValue("tab", true);
        // 削除完了メッセージを表示する。
        setMsg(getClientMessage("B100190_003"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (rowData: Data) => {
    meiMethods.reset();
    // タブを編集にする
    meiMethods.setValue("tab", false);
    // ダブルクリックした行を選択状態にする
    setRowData(rowData);
    // 明細入力部にデータを表示する。
    meiMethods.setValue("locationCdF", rowData.locationCdF);
    meiMethods.setValue("ren", rowData.ren);
    meiMethods.setValue("dan", rowData.dan);
    meiMethods.setValue("maguti", rowData.maguti);
    meiMethods.setValue(
      "nyukolocationKbn",
      getSCode(NyukolocationKbn, rowData.nyukolocationKbn!)
    );
    meiMethods.setValue(
      "shukolocationKbn",
      getSCode(ShukolocationKbn, rowData.shukolocationKbn!)
    );
    meiMethods.setValue("locationCd", rowData.locationCd);
    meiMethods.setValue("version", rowData.version);
  };

  // タブ切り替え
  const handleChangeTab = () => {
    // 行選択解除
    setRowData(undefined);
    // 明細入力部の値を初期化
    meiMethods.reset();
  };

  // ロケーションコードをフォーマットする(数字のみ)
  const formatLocationCd = () => {
    // データ取得
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();
    const beforeLocationCd = [
      fields.headerZoneCd!,
      fields.headerRetu!,
      fields.headerRen!,
      meiFields.dan!,
      meiFields.maguti!,
    ];
    const locationCd = beforeLocationCd.join("");

    return locationCd;
  };

  // ロケーションコードをフォーマットする（ハイフンつき）
  const formatHaihunLocationCd = () => {
    // データ取得
    const fields = methods.getValues();
    const meiFields = meiMethods.getValues();
    const beforeLocationCd = [
      fields.headerZoneCd!,
      fields.headerRetu!,
      fields.headerRen!,
      meiFields.dan!,
      meiFields.maguti!,
    ];
    const locationCd = beforeLocationCd.join("-");

    return locationCd;
  };

  // 新規登録行の追加
  const addRow = (newData: B100190CreateRequest) => {
    const gyoNo: number = rows.length + 1;
    const newRowData: Data = {
      gyoNo: gyoNo,
      locationCdF: formatHaihunLocationCd(),
      locationCd: formatLocationCd(),
      ren: newData.headerRen,
      dan: newData.dan,
      maguti: newData.maguti,
      nyukolocationKbn: newData.nyukolocationKbn,
      shukolocationKbn: newData.shukolocationKbn,
      version: 1,
    };

    setRows([...rows, newRowData]);
  };

  // 行の更新
  const updateRow = (rowData: any, fields: Fields, meiFields: MeiFields) => {
    // 明細入力部の反映
    const gyoNo: number = rowData.gyoNo!;
    const changedRowData: Data = {
      gyoNo: gyoNo,
      locationCdF: formatHaihunLocationCd(),
      locationCd: formatLocationCd(),
      ren: fields.headerRen,
      dan: meiFields.dan,
      maguti: meiFields.maguti,
      nyukolocationKbn: meiFields.nyukolocationKbn?.value,
      shukolocationKbn: meiFields.shukolocationKbn?.value,
      version: meiFields.version! + 1,
    };

    // 明細を設定
    rows[gyoNo - 1] = changedRowData;
    setRows([...rows]);
  };

  //excelボタンクリック時
  const handleMoveOutput = () => {
    // ロケーション管理マスタ出力画面へ遷移する。
    setMenuNavigate("next");
    navigate("/b100193");
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="ロケーション管理マスタメンテ"
          gridTemplateColumns="4rem 3rem 40rem 5rem 5rem"
        >
          <SText gridColumn="span 4"></SText>
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            {...register(5)}
            onClick={handleMoveOutput}
          />
          <SLabel required gridColumn="1">
            ゾーン
          </SLabel>
          <STextField
            name="headerZoneCd"
            label="ゾーン"
            code
            required
            size={2}
            {...register(1)}
          />
          <SLabel required gridColumn="1">
            列
          </SLabel>
          <STextField
            name="headerRetu"
            label="列"
            code
            required
            size={2}
            {...register(2)}
          />
          <SLabel required gridColumn="1">
            連
          </SLabel>
          <STextField
            name="headerRen"
            label="連"
            code
            required
            size={2}
            {...register(3)}
          />
          <SNormalButton
            gridColumn="4"
            gridRow="5"
            onClick={handleClickSearchButton}
            label={labels.buttons.search}
            align="right"
            {...register(4)}
          />
        </SFrame>
      </FormProvider>

      <SFrame>
        <STable
          keyName="gyoNo"
          data={rows}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
          width={900}
          height={430}
          pageSize={15}
        />
      </SFrame>

      <FormProvider {...meiMethods}>
        <SFrame>
          <SEntryTab name="tab" handleOk={handleChangeTab}></SEntryTab>
          <SFrame gridTemplateColumns="14rem 5rem 5rem 6rem 10rem 10rem">
            <SLabel>ロケーション</SLabel>
            <SLabel>連</SLabel>
            <SLabel required>段</SLabel>
            <SLabel required>間</SLabel>
            <SLabel>入庫ロケ区分</SLabel>
            <SLabel>出庫ロケ区分</SLabel>

            <STextField
              name="locationCdF"
              label="ロケーション"
              size={14}
              disabled
            />
            <STextField name="ren" label="連" size={2} disabled />
            <STextField name="dan" label="段" code size={2} {...register(7)} />
            <STextField
              name="maguti"
              label="間"
              code
              size={2}
              {...register(8)}
            />

            <SCodeSelect
              name="nyukolocationKbn"
              options={NyukolocationKbn}
              {...register(9)}
            />
            <SCodeSelect
              name="shukolocationKbn"
              options={ShukolocationKbn}
              {...register(10)}
            />

            {meiMethods.getValues().tab === true ? (
              <>
                <SNormalButton
                  label={labels.buttons.create}
                  gridRow="3"
                  gridColumn="6"
                  align="right"
                  {...register(11)}
                  dialog={
                    <SConfirmDialog
                      title={labels.dialog.createMasterTitle}
                      msg={labels.dialog.createConfirm}
                    />
                  }
                  dialogAction="confirm"
                  handleSubmit={handleCreate}
                />
              </>
            ) : (
              <>
                <SCancelButton
                  label={labels.buttons.linedeletion}
                  gridRow="3"
                  align="left"
                  {...register(12)}
                  onClick={handleOpenDeleteConfirm}
                />
                <SNormalButton
                  label={labels.buttons.update}
                  gridRow="3"
                  gridColumn="6"
                  align="right"
                  {...register(13)}
                  onClick={handleOpenUpdateConfirm}
                />
              </>
            )}
          </SFrame>
        </SFrame>
        <SConfirmDialogAction
          title={labels.dialog.updateMasterTitle}
          msg={labels.dialog.updateConfirm}
          onOk={handleUpdate}
          onCancel={handleCancelUpdateConfirm}
          isOpen={openUpdateConfirm}
        />
        <SConfirmDialogAction
          title={labels.dialog.deleteMasterTitle}
          msg={labels.dialog.deleteConfirm}
          onOk={handleDelete}
          onCancel={handleCancelDeleteConfirm}
          isOpen={openDeleteConfirm}
        />
      </FormProvider>
    </>
  );
};
